/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


const styles = css`
  :host {
    display: block;
  }

  input {
    appearance: none;
    background: transparent;
    cursor: pointer;
    display: block;
    height: 16px;
    margin: 0;
    width: 100%;
  }

  input::-webkit-slider-runnable-track {
    background: linear-gradient(to right, var(--primary) 0 var(--slider-value, 50%), var(--muted) var(--slider-value, 50%) 100%);
    border-radius: 999px;
    height: 6px;
  }

  input::-moz-range-track {
    background: linear-gradient(to right, var(--primary) 0 var(--slider-value, 50%), var(--muted) var(--slider-value, 50%) 100%);
    border-radius: 999px;
    height: 6px;
  }

  input::-webkit-slider-thumb {
    appearance: none;
    background: var(--background, #ffffff);
    border: 1px solid var(--primary, #18181b);
    border-radius: 999px;
    height: 16px;
    margin-top: -5px;
    width: 16px;
  }

  input::-moz-range-thumb {
    background: var(--background, #ffffff);
    border: 1px solid var(--primary, #18181b);
    border-radius: 999px;
    height: 16px;
    width: 16px;
  }

  input:focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  input:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  :host([orientation="vertical"]) { display: inline-block; height: 176px; width: auto; }
  :host([orientation="vertical"]) input { height: 176px; width: 16px; writing-mode: vertical-lr; direction: rtl; }
`;

export class AppSlider extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSlider)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["min","data-min","max","data-max","step","data-step","value","data-value","disabled","data-disabled","orientation","data-orientation"];

  #min = "0";
  #max = "100";
  #step = "1";
  #value = "50";
  #disabled = false;
  #orientation = "horizontal";
  #minSet = false;
  #maxSet = false;
  #stepSet = false;
  #valueSet = false;
  #disabledSet = false;
  #orientationSet = false;
  #attr0;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #input = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="input"]') : null; } }; })();
  #hooksLive = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get min() {
    return this.#min;
  }

  set min(value) {
    const raw = value ?? "0";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#min, next)) return;
    this.#min = next;
    this.#minSet = true;
    this.#updateBindings('min');
  }

  get max() {
    return this.#max;
  }

  set max(value) {
    const raw = value ?? "100";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#max, next)) return;
    this.#max = next;
    this.#maxSet = true;
    this.#updateBindings('max');
  }

  get step() {
    return this.#step;
  }

  set step(value) {
    const raw = value ?? "1";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#step, next)) return;
    this.#step = next;
    this.#stepSet = true;
    this.#updateBindings('step');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? "50";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#disabled, next)) return;
    this.#disabled = next;
    this.#disabledSet = true;
    this.#updateBindings('disabled');
  }

  get orientation() {
    return this.#orientation;
  }

  set orientation(value) {
    const raw = value ?? "horizontal";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#orientation, next)) return;
    this.#orientation = next;
    this.#orientationSet = true;
    this.#updateBindings('orientation');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-min')) this.min = this.dataset.min;
    if (this.hasAttribute('data-max')) this.max = this.dataset.max;
    if (this.hasAttribute('data-step')) this.step = this.dataset.step;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
    if (this.hasAttribute('data-disabled')) this.disabled = this.dataset.disabled;
    if (this.hasAttribute('data-orientation')) this.orientation = this.dataset.orientation;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.min = props.min;
      this.max = props.max;
      this.step = props.step;
      this.value = props.value;
      this.disabled = props.disabled;
      this.orientation = props.orientation;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#minSet) this.min = this.dataset.min;
    if (!this.#maxSet) this.max = this.dataset.max;
    if (!this.#stepSet) this.step = this.dataset.step;
    if (!this.#valueSet) this.value = this.dataset.value;
    if (!this.#disabledSet) this.disabled = this.dataset.disabled;
    if (!this.#orientationSet) this.orientation = this.dataset.orientation;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'min':
      case 'data-min':
        this.min = newValue;
        break;
      case 'max':
      case 'data-max':
        this.max = newValue;
        break;
      case 'step':
      case 'data-step':
        this.step = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
      case 'disabled':
      case 'data-disabled':
        this.disabled = newValue;
        break;
      case 'orientation':
      case 'data-orientation':
        this.orientation = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><input data-argon-ref="input" type="range" min="${this.#escAttr(this.#min)}" max="${this.#escAttr(this.#max)}" step="${this.#escAttr(this.#step)}" value="${this.#escAttr(this.#value)}" data-argon="2611" data-argon-bind="0">`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    const el = this.#input.current;
    if (!el)
        return;
    el.toggleAttribute("disabled", this.#disabled);
    const low = Number(this.#min);
    const high = Number(this.#max);
    const current = Number(this.#value);
    const percentage = high > low ? Math.max(0, Math.min(100, ((current - low) / (high - low)) * 100)) : 0;
    el.style.setProperty("--slider-value", `${percentage}%`);
    this.setAttribute("orientation", this.#orientation);
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if ((prop === undefined || prop === 'min') && this.#attr0) {
      const __argonNext = `${this.#min}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('min', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'max') && this.#attr0) {
      const __argonNext = `${this.#max}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('max', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'step') && this.#attr0) {
      const __argonNext = `${this.#step}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('step', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'value') && this.#attr0) {
      const __argonNext = `${this.#value}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr0.setAttribute('value', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'disabled' || prop === 'min' || prop === 'max' || prop === 'value' || prop === 'orientation')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2611').forEach(el => this.#listen(el, '2611', 'input', (event: Event) => {
    const next = (event.target as HTMLInputElement).value;
    this.dispatchEvent(new CustomEvent("value-change", { bubbles: true, composed: true, detail: { value: Number(next) } }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
